<template>
  <section class="state-map">
    <div class="state-map__head">
      <h3 class="state-map__title">State Map</h3>
      <p class="state-map__sub">
        {{ unlockedCount }} of {{ total }} unlocked · Sign up a donor from a new state to fill it in
      </p>
    </div>

    <GivarooSpinner v-if="loading" class="mt-8" />

    <div v-else class="state-map__wrap" @mousemove="onMove" @mouseleave="hideTip">
      <svg
        ref="svgEl"
        class="state-map__svg"
        xmlns="http://www.w3.org/2000/svg"
        :viewBox="viewBox"
        role="img"
        aria-label="United States map of unlocked donor states"
      >
        <g v-for="loc in locations" :key="loc.id">
          <path
            :d="loc.path"
            class="state-map__state"
            :class="{ 'state-map__state--on': isUnlocked(loc.id) }"
            :style="isUnlocked(loc.id) ? { fill: colorFor(loc.id) } : null"
            :data-code="loc.id"
            :aria-label="stateLabel(loc)"
            @mouseenter="showTip(loc, $event)"
            @focus="showTip(loc, $event)"
            @blur="hideTip"
            tabindex="0"
          />
          <text
            v-if="labelCenters[loc.id] && !labelCenters[loc.id].callout"
            class="state-map__abbr"
            :class="{ 'state-map__abbr--on': isUnlocked(loc.id) }"
            :x="labelCenters[loc.id].x"
            :y="labelCenters[loc.id].y"
            text-anchor="middle"
            dominant-baseline="central"
            aria-hidden="true"
          >
            {{ loc.id.toUpperCase() }}
          </text>
        </g>

        <g v-for="loc in calloutLocs" :key="'co-' + loc.id" class="state-map__callout">
          <line
            class="state-map__leader"
            :x1="labelCenters[loc.id].ax"
            :y1="labelCenters[loc.id].ay"
            :x2="labelCenters[loc.id].x - 4"
            :y2="labelCenters[loc.id].y"
          />
          <text
            class="state-map__abbr state-map__abbr--callout"
            :class="{ 'state-map__abbr--on': isUnlocked(loc.id) }"
            :x="labelCenters[loc.id].x"
            :y="labelCenters[loc.id].y"
            text-anchor="start"
            dominant-baseline="central"
            :aria-label="stateLabel(loc)"
            @mouseenter="showTip(loc, $event)"
            @focus="showTip(loc, $event)"
            @blur="hideTip"
            tabindex="0"
          >
            {{ loc.id.toUpperCase() }}
          </text>
        </g>
      </svg>

      <div
        v-if="tip"
        class="state-map__tip"
        role="tooltip"
        :style="{ left: tip.x + 'px', top: tip.y + 'px' }"
      >
        <strong>{{ tip.name }}</strong>
        <template v-if="tip.unlocked">
          <span>Givaroo ID: {{ tip.givarooId }}</span>
          <span>{{ tip.date }}</span>
        </template>
        <span v-else class="state-map__tip-muted">Not unlocked yet</span>
      </div>
    </div>
  </section>
</template>

<script>
import { computed, nextTick, onMounted, ref } from 'vue'
import USA from '@svg-maps/usa'
import GivarooSpinner from '@/components/GivarooSpinner.vue'
import { achievementFunctions } from '@/services/achievements'

const PALETTE = [
  '#2F7D6B',
  '#B57E14',
  '#7A5CD0',
  '#42A5F5',
  '#EF5350',
  '#26C6DA',
  '#FF7043',
  '#66BB6A',
  '#EC407A',
  '#5C6BC0',
]

// States too small (or too coastal) to hold a 2-letter label on the landmass.
// They get a stacked callout in the Atlantic with a leader line.
const FORCE_CALLOUT = new Set(['vt', 'nh', 'ma', 'ri', 'ct', 'nj', 'de', 'md', 'dc'])

// Extra room on the right of the original map for those callouts.
const VIEW_PAD_RIGHT = 78
const CALLOUT_GAP = 16

const expandViewBox = (raw, padRight) => {
  const parts = String(raw)
    .trim()
    .split(/[\s,]+/)
    .map(Number)
  if (parts.length !== 4 || parts.some((n) => Number.isNaN(n))) return raw
  return `${parts[0]} ${parts[1]} ${parts[2] + padRight} ${parts[3]}`
}

const viewBoxRight = (raw) => {
  const parts = String(raw)
    .trim()
    .split(/[\s,]+/)
    .map(Number)
  if (parts.length !== 4 || parts.some((n) => Number.isNaN(n))) return 1220
  return parts[0] + parts[2]
}

const pointInFill = (path, x, y) => {
  if (typeof path.isPointInFill !== 'function') return true
  try {
    if (typeof DOMPoint === 'function') return path.isPointInFill(new DOMPoint(x, y))
  } catch (err) {
    // Safari / older SVG implementations want an SVGPoint.
  }
  const svg = path.ownerSVGElement
  if (!svg || typeof svg.createSVGPoint !== 'function') return true
  const pt = svg.createSVGPoint()
  pt.x = x
  pt.y = y
  return path.isPointInFill(pt)
}

const circleInFill = (path, x, y, radius) => {
  const steps = 8
  for (let i = 0; i < steps; i++) {
    const angle = (Math.PI * 2 * i) / steps
    if (!pointInFill(path, x + Math.cos(angle) * radius, y + Math.sin(angle) * radius)) return false
  }
  return true
}

const inscribedRadius = (path, x, y, maxR) => {
  if (!pointInFill(path, x, y)) return -1
  let lo = 0
  let hi = Math.max(maxR, 0)
  for (let i = 0; i < 7; i++) {
    const mid = (lo + hi) / 2
    if (circleInFill(path, x, y, mid)) lo = mid
    else hi = mid
  }
  return lo
}

// Prefer the largest ring (mainland / biggest island) so panhandles, keys, and
// Great Lakes islands don't pull the search box into the water.
const largestLandmassBox = (path) => {
  const full = path.getBBox()
  const d = path.getAttribute('d') || ''
  // Only split on absolute M so relative `m` islands keep their parent origin.
  const parts = d.split(/(?=M)/).filter((p) => p && p.length > 4)
  if (parts.length <= 1) return full

  const svg = path.ownerSVGElement
  if (!svg) return full
  const tmp = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  svg.appendChild(tmp)
  let best = full
  let bestArea = 0
  for (const part of parts) {
    tmp.setAttribute('d', part)
    const box = tmp.getBBox()
    const area = box.width * box.height
    if (area > bestArea) {
      bestArea = area
      best = { x: box.x, y: box.y, width: box.width, height: box.height }
    }
  }
  tmp.remove()
  return best
}

// Approximate pole of inaccessibility: the in-fill point farthest from the
// edge. That sits visually in the "body" of Idaho, the LP of Michigan, the
// Florida peninsula, etc. — unlike getBBox() which is pulled by panhandles.
const visualCenter = (path) => {
  const box = largestLandmassBox(path)
  const minSide = Math.min(box.width, box.height)
  const fallback = { x: box.x + box.width / 2, y: box.y + box.height / 2, r: 0 }
  if (minSide < 1) return fallback

  let best = { ...fallback, r: -1 }
  const maxR = minSide / 2
  const search = (x0, y0, x1, y1, step) => {
    for (let y = y0; y <= y1; y += step) {
      for (let x = x0; x <= x1; x += step) {
        const r = inscribedRadius(path, x, y, maxR)
        if (r > best.r) best = { x, y, r }
      }
    }
  }

  const coarse = Math.max(minSide / 8, 3)
  search(box.x, box.y, box.x + box.width, box.y + box.height, coarse)
  if (best.r >= 0) {
    const pad = coarse
    const fine = Math.max(coarse / 3, 1.5)
    search(best.x - pad, best.y - pad, best.x + pad, best.y + pad, fine)
  } else {
    return fallback
  }
  return best
}

export default {
  name: 'UsStatesMap',
  components: { GivarooSpinner },
  setup() {
    const { api } = achievementFunctions()
    const loading = ref(true)
    const byCode = ref({})
    const tip = ref(null)
    const svgEl = ref(null)
    const labelCenters = ref({})

    const map = USA.default || USA
    const viewBox = expandViewBox(map.viewBox, VIEW_PAD_RIGHT)
    const locations = map.locations

    const total = computed(() => Object.keys(byCode.value).length || 51)
    const unlockedCount = computed(
      () => Object.values(byCode.value).filter((s) => s.unlocked).length,
    )
    const calloutLocs = computed(() =>
      locations.filter((loc) => labelCenters.value[loc.id]?.callout),
    )

    const isUnlocked = (id) => !!byCode.value[id.toUpperCase()]?.unlocked

    const colorFor = (id) => {
      const code = id.toUpperCase()
      let hash = 0
      for (let i = 0; i < code.length; i++) hash = (hash + code.charCodeAt(i) * (i + 1)) % 997
      return PALETTE[hash % PALETTE.length]
    }

    const stateLabel = (loc) => {
      const info = byCode.value[loc.id.toUpperCase()]
      if (info?.unlocked) return `${info.name}, unlocked`
      return `${loc.name}, locked`
    }

    const placeLabels = () => {
      const svg = svgEl.value
      if (!svg) return
      const next = {}
      const callouts = []
      svg.querySelectorAll('path.state-map__state[data-code]').forEach((path) => {
        const id = path.getAttribute('data-code')
        if (!id) return
        const center = visualCenter(path)
        const item = {
          x: center.x,
          y: center.y,
          ax: center.x,
          ay: center.y,
          callout: FORCE_CALLOUT.has(id) || center.r < 8,
        }
        next[id] = item
        if (item.callout) callouts.push({ id, ...item })
      })

      callouts.sort((a, b) => a.ay - b.ay || a.ax - b.ax)
      const labelX = viewBoxRight(map.viewBox) + 20
      let y = callouts.length ? callouts[0].ay : 0
      callouts.forEach((c, i) => {
        if (i > 0) y = Math.max(c.ay, y + CALLOUT_GAP)
        next[c.id] = { ...next[c.id], x: labelX, y, callout: true }
      })

      labelCenters.value = next
    }

    const showTip = (loc, event) => {
      const info = byCode.value[loc.id.toUpperCase()]
      const wrap = event.currentTarget.ownerSVGElement?.parentElement
      const rect = wrap?.getBoundingClientRect()
      const clientX = event.clientX ?? rect?.left ?? 0
      const clientY = event.clientY ?? rect?.top ?? 0
      tip.value = {
        name: info?.name || loc.name,
        unlocked: !!info?.unlocked,
        givarooId: info?.givarooId,
        date: info?.unlockedAt
          ? new Date(info.unlockedAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '',
        x: clientX - (rect?.left || 0) + 12,
        y: clientY - (rect?.top || 0) + 12,
      }
    }

    const onMove = (event) => {
      if (!tip.value) return
      const wrap = event.currentTarget
      const rect = wrap.getBoundingClientRect()
      tip.value = {
        ...tip.value,
        x: event.clientX - rect.left + 12,
        y: event.clientY - rect.top + 12,
      }
    }

    const hideTip = () => {
      tip.value = null
    }

    onMounted(async () => {
      try {
        const data = await api.csc.getStates()
        const mapByCode = {}
        for (const s of data.states || []) {
          mapByCode[s.code] = s
        }
        byCode.value = mapByCode
      } catch (err) {
        console.error('Failed to load state map:', err)
      } finally {
        loading.value = false
        await nextTick()
        placeLabels()
      }
    })

    return {
      loading,
      svgEl,
      viewBox,
      locations,
      labelCenters,
      calloutLocs,
      total,
      unlockedCount,
      tip,
      isUnlocked,
      colorFor,
      stateLabel,
      showTip,
      onMove,
      hideTip,
    }
  },
}
</script>

<style scoped>
.state-map {
  margin-top: 1.5rem;
  padding: 1rem 1rem 1.25rem;
  border: 1px solid #eceef1;
  border-radius: 0.85rem;
  background: #fff;
}

.state-map__head {
  margin-bottom: 0.75rem;
}

.state-map__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
  color: #1f2937;
}

.state-map__sub {
  margin: 0.25rem 0 0;
  font-size: 0.8125rem;
  color: #6b7280;
}

.state-map__wrap {
  position: relative;
  width: 100%;
}

.state-map__svg {
  display: block;
  width: 100%;
  height: auto;
  max-height: 35rem;
  overflow: visible;
}

.state-map__state {
  fill: #d1d5db;
  stroke: #fff;
  stroke-width: 1.25;
  cursor: pointer;
  transition: fill 0.15s ease, opacity 0.15s ease;
  outline: none;
}

.state-map__state:hover,
.state-map__state:focus-visible {
  opacity: 0.88;
  stroke: #111827;
  stroke-width: 1.75;
}

.state-map__state--on {
  /* fill set inline from palette */
}

.state-map__abbr {
  fill: #4b5563;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  pointer-events: none;
  user-select: none;
  paint-order: stroke;
  stroke: rgba(255, 255, 255, 0.9);
  stroke-width: 2.5px;
}

.state-map__abbr--on {
  fill: #111827;
}

.state-map__abbr--callout {
  font-size: 11px;
  pointer-events: auto;
  cursor: pointer;
  outline: none;
}

.state-map__leader {
  fill: none;
  stroke: #9ca3af;
  stroke-width: 0.9;
  pointer-events: none;
}

.state-map__tip {
  position: absolute;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 9rem;
  max-width: 14rem;
  padding: 0.55rem 0.7rem;
  border-radius: 0.5rem;
  background: #111827;
  color: #fff;
  font-size: 0.75rem;
  line-height: 1.3;
  pointer-events: none;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.22);
  transform: translate(0, 0);
}

.state-map__tip strong {
  font-size: 0.8125rem;
}

.state-map__tip-muted {
  color: #9ca3af;
}
</style>
